import React, { useMemo, useState } from 'react';
import { Alert, Button, DatePicker, Form, Input, Select, Typography } from 'antd';
import { post as rawPost } from 'axios';
import moment from 'moment';
import axios from '../../helpers/Axios';

const { Paragraph, Text, Link } = Typography;

const formatTinDisplay = (raw = '') => {
  const digits = raw.replace(/\D/g, '').slice(0, 9);
  if (!digits) return '';
  const parts = digits.match(/.{1,3}/g) || [];
  return digits.length === 9 ? `${parts.join('-')}-00000` : parts.join('-');
};

const normalizeTin = (value = '') => value.replace(/\D/g, '').slice(0, 9);

const TinBday = ({
  tin_bday = {},
  user_details = {},
  companyId,
  toggleUpdateInfo,
  alert: parentAlert,
}) => {
  const [form] = Form.useForm();
  const [visible, setVisible] = useState(false);
  const [responseType, setResponseType] = useState(null);
  const [message, setMessage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [tinNumber, setTinNumber] = useState(
    tin_bday?.is_tin_match === false ? tin_bday?.tin || '' : ''
  );

  const initialBirthdate = tin_bday?.birthdate ? moment(tin_bday.birthdate) : moment();

  const showAlert = (type, content) => {
    setVisible(true);
    setResponseType(type);
    setMessage(content);
    window.scrollTo({ top: 0, behavior: 'smooth' });

    if (type === 'Success') {
      setTimeout(() => setVisible(false), 10000);
    }
  };

  const handleTinChange = (e) => {
    const formattedValue = formatTinDisplay(e.target.value);
    setTinNumber(formattedValue);
    form.setFieldValue('tin', formattedValue);
  };

  const tinNumberActual = normalizeTin(tinNumber);

  const formatBirthdate = (dateValue) => {
    return moment(dateValue).format('YYYY-MM-DD');
  };

  const buildRequestBody = ({ firstName, lastName, gender, birthdate }) => ({
    tin: tinNumberActual,
    birthdate: formatBirthdate(birthdate),
    branchCode: '00000',
    firstName: (firstName || '').toUpperCase(),
    lastName: (lastName || '').toUpperCase(),
    gender,
  });

  const successUpdateFlow = async (birthdate) => {
    await axios.post('/api/update/user/information/', {
      tin: tinNumberActual,
      birth_date: formatBirthdate(birthdate),
      user_id: localStorage.getItem('user_id'),
    });

    const resolvedCompanyId = companyId || localStorage.getItem('user_company');
    const res = await axios.get(`/api/get_company/?id=${resolvedCompanyId}`);

    toggleUpdateInfo?.();
    parentAlert?.('Success', res?.data?.message || 'Information updated successfully');
  };

  const errorMessage = (
    <>
      Your provided information does not match any of our records. Please visit the{' '}
      <a target="_blank" rel="noopener noreferrer" href="https://orus.bir.gov.ph/home">
        BIR Online Registration and Update System (ORUS)
      </a>{' '}
      or check on{' '}
      <a target="_blank" rel="noopener noreferrer" href="https://revie.bir.gov.ph/tin-validation">
        REVIE
      </a>{' '}
      to check your details. You may also check your record with the nearest BIR Revenue District Office.
    </>
  );

  const tinNotFoundMessage = (
    <>
      Your provided TIN does not match any of our records. Please visit the{' '}
      <a target="_blank" rel="noopener noreferrer" href="https://orus.bir.gov.ph/home">
        BIR Online Registration and Update System (ORUS)
      </a>{' '}
      or check on{' '}
      <a target="_blank" rel="noopener noreferrer" href="https://revie.bir.gov.ph/tin-validation">
        REVIE
      </a>{' '}
      to check your details. You may also check your record with the nearest BIR Revenue District Office.
    </>
  );

  const buildSuffixCombos = (firstName, lastName) => {
    const suffixRaw = (user_details?.filer_suffix || '').toUpperCase().trim();
    if (!suffixRaw) return [];

    const suffixWithDot = suffixRaw.endsWith('.') ? suffixRaw : `${suffixRaw}.`;
    const suffixNoDot = suffixRaw.replace(/\./g, '');

    const apiFirst = (firstName || '').toUpperCase().replace(suffixRaw, '').trim();
    const apiLast = (lastName || '').toUpperCase().replace(suffixRaw, '').trim();

    const combos = [
      { firstName: `${apiFirst} ${suffixRaw}`.trim(), lastName: apiLast },
      { firstName: apiFirst, lastName: `${apiLast} ${suffixRaw}`.trim() },
    ];

    if (suffixRaw !== suffixNoDot) {
      combos.push(
        { firstName: `${apiFirst} ${suffixNoDot}`.trim(), lastName: apiLast },
        { firstName: apiFirst, lastName: `${apiLast} ${suffixNoDot}`.trim() }
      );
    }

    if (suffixRaw !== suffixWithDot) {
      combos.push(
        { firstName: `${apiFirst} ${suffixWithDot}`.trim(), lastName: apiLast },
        { firstName: apiFirst, lastName: `${apiLast} ${suffixWithDot}`.trim() }
      );
    }

    return combos;
  };

  const trySuffixCombos = async (values, combosToTry, index = 0) => {
    if (index >= combosToTry.length) {
      return false;
    }

    const attempt = combosToTry[index];

    const requestBodyWithSuffix = buildRequestBody({
      ...values,
      firstName: attempt.firstName,
      lastName: attempt.lastName,
    });

    const res = await axios.post(
      '/api/tin/individual/query/',
      requestBodyWithSuffix,
      {
        validateStatus: () => true,
      }
    );

    if (res?.data?.description === 'TIN FOUND') {
      await successUpdateFlow(values.birthdate);
      return true;
    }

    const retryableMismatch =
      res?.status === 404 &&
      (
        res?.data?.description?.includes('MISMATCHED INFO') ||
        res?.data?.description === 'TIN NOT_FOUND'
      );

    if (!retryableMismatch) {
      return false;
    }

    return trySuffixCombos(values, combosToTry, index + 1);
  };

  const handleSubmit = async (values) => {
    try {
      setSubmitting(true);

      const requestBody = buildRequestBody(values);

      const res = await axios.post(
        '/api/tin/individual/query/',
        requestBody,
        {
          validateStatus: () => true,
        }
      );

      if (res?.data?.description === 'TIN FOUND') {
        await successUpdateFlow(values.birthdate);
        return;
      }

      if (res?.status === 404 && res?.data?.description?.includes('Birthdate')) {
        showAlert('Failed', errorMessage);
        return;
      }

      if (res?.status === 404 && res?.data?.description?.includes('MISMATCHED INFO')) {
        const combosToTry = buildSuffixCombos(values.firstName, values.lastName);
        const matched = await trySuffixCombos(values, combosToTry);

        if (!matched) {
          showAlert('Failed', errorMessage);
        }
        return;
      }

      if (res?.status === 404 && res?.data?.description === 'TIN NOT_FOUND') {
        showAlert('Failed', tinNotFoundMessage);
        return;
      }

      showAlert('Failed', errorMessage);
    } catch (error) {
      showAlert('Failed', 'Something went wrong while validating your information.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: 'relative',
        minHeight: '100%',
        background: '#f5f7fb',
        overflow: 'hidden',
        padding: 20,
      }}
    >
      {/* subtle background shapes */}
      <div
        style={{
          position: 'absolute',
          top: 110,
          right: -30,
          width: 120,
          height: 120,
          borderRadius: '50%',
          background: 'rgba(34, 197, 94, 0.08)',
          filter: 'blur(2px)',
          zIndex: 0,
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: 70,
          right: 30,
          width: 140,
          height: 140,
          borderRadius: '50%',
          background: 'rgba(34, 197, 94, 0.12)',
          zIndex: 0,
        }}
      />

      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* intro card */}
        <div
          style={{
            background: '#ffffff',
            borderRadius: 16,
            padding: 18,
            boxShadow: '0 10px 30px rgba(15, 23, 42, 0.05)',
            border: '1px solid #e5e7eb',
            marginBottom: 18,
          }}
        >
          {tin_bday?.is_tin_match !== false ? (
            <>
              <Paragraph style={{ marginBottom: 10, fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                <Text strong style={{ color: '#dc2626' }}>*** </Text>
                To continue using eFAST system, please ensure that your profile information is updated,
                including your BIR-issued Taxpayer Identification Number (TIN), then click <b>"Submit"</b> to confirm.
              </Paragraph>

              <Paragraph style={{ marginBottom: 10, fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Kindly note that the required TIN should be your TIN and not your company’s TIN.
              </Paragraph>

              <Paragraph style={{ marginBottom: 0, fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Go to{' '}
                <Link href="https://orus.bir.gov.ph/home" target="_blank" style={{ fontWeight: 600, color: '#0f766e' }}>
                  ORUS
                </Link>{' '}
                to validate your TIN. If ORUS is unavailable, see the notice{' '}
                <Link
                  href="https://www.sec.gov.ph/notices/alternative-platform-of-bir-for-tin-validation/"
                  target="_blank"
                  style={{ fontWeight: 600, color: '#0f766e' }}
                >
                  “TIN Validation for eSECURE and eFAST” posted on March 13, 2025 for details.
                </Link>
              </Paragraph>
            </>
          ) : (
            <Paragraph style={{ marginBottom: 0, fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
              <Text strong style={{ color: '#dc2626' }}>*** </Text>
              Your information doesn’t match BIR records. Please update your profile and click Submit once all details are correct.
              Use your personal TIN (not company TIN). Go to{' '}
              <Link href="https://orus.bir.gov.ph/home" target="_blank" style={{ fontWeight: 600, color: '#0f766e' }}>
                ORUS
              </Link>{' '}
              to validate your TIN. If unavailable, see the notice{' '}
              <Link
                href="https://www.sec.gov.ph/notices/alternative-platform-of-bir-for-tin-validation/"
                target="_blank"
                style={{ fontWeight: 600, color: '#0f766e' }}
              >
                “TIN Validation for eSECURE and eFAST” posted on March 13, 2025 for details.
              </Link>
              .
            </Paragraph>
          )}
        </div>

        {/* alert */}
        {visible && (
          <Alert
            type={responseType === 'Success' ? 'success' : responseType === 'Invalid' ? 'warning' : 'error'}
            message={message}
            closable={responseType !== 'Success'}
            onClose={() => setVisible(false)}
            style={{
              marginBottom: 16,
              borderRadius: 12,
            }}
          />
        )}

        {/* form card */}
        <div
          style={{
            background: '#ffffff',
            borderRadius: 16,
            padding: 18,
            boxShadow: '0 10px 30px rgba(15, 23, 42, 0.05)',
            border: '1px solid #e5e7eb',
          }}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              gender: 'male',
              firstName: (localStorage.getItem('user_first_name') || '').toUpperCase(),
              lastName: (localStorage.getItem('user_last_name') || '').toUpperCase(),
              birthdate: initialBirthdate,
              tin: tinNumber,
            }}
          >
            <Form.Item
              name="firstName"
              label={<span style={{ fontWeight: 600, color: '#374151' }}>First Name</span>}
              rules={[{ required: true, message: 'Firstname is required' }]}
              style={{ marginBottom: 14 }}
            >
              <Input
                placeholder="Enter your firstname."
                disabled
                style={{ height: 44, borderRadius: 10 }}
              />
            </Form.Item>

            <Form.Item
              name="lastName"
              label={<span style={{ fontWeight: 600, color: '#374151' }}>Last Name</span>}
              rules={[{ required: true, message: 'Last name is required' }]}
              style={{ marginBottom: 14 }}
            >
              <Input
                placeholder="Enter your lastname."
                disabled
                style={{ height: 44, borderRadius: 10 }}
              />
            </Form.Item>

            <Form.Item
              name="gender"
              label={<span style={{ fontWeight: 600, color: '#374151' }}>Gender</span>}
              rules={[{ required: true, message: 'Gender is required' }]}
              style={{ marginBottom: 14 }}
            >
              <Select
                placeholder="Select your gender."
                options={[
                  { value: 'male', label: 'Male' },
                  { value: 'female', label: 'Female' },
                ]}
                style={{ width: '100%' }}
              />
            </Form.Item>

            <Form.Item
              name="birthdate"
              label={<span style={{ fontWeight: 600, color: '#374151' }}>Birth Date</span>}
              rules={[{ required: true, message: 'Birth date is required' }]}
              style={{ marginBottom: 14 }}
            >
              <DatePicker
                style={{ width: '100%', height: 44, borderRadius: 10 }}
                format="MM/DD/YYYY"
                allowClear={false}
                maxDate={moment()}
              />
            </Form.Item>

            <Form.Item
              name="tin"
              label={<span style={{ fontWeight: 600, color: '#374151' }}>Issued User TIN</span>}
              rules={[
                { required: true, message: 'TIN number is required' },
                {
                  pattern: /^[0-9]{3}-[0-9]{3}-[0-9]{3}-00000$/,
                },
              ]}
              style={{ marginBottom: 10 }}
            >
              <Input
                autoComplete="off"
                placeholder="xxx-xxx-xxx-00000"
                maxLength={19}
                style={{ height: 44, borderRadius: 10 }}
                onChange={(e) => {
                  const formattedValue = formatTinDisplay(e.target.value);
                  setTinNumber(formattedValue);
                  form.setFieldsValue({ tin: formattedValue })
                }}
              />
            </Form.Item>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                disabled={submitting}
                style={{
                  height: 42,
                  minWidth: 96,
                  borderRadius: 10,
                  border: 'none',
                  background: '#0f8269',
                  boxShadow: '0 8px 18px rgba(22, 163, 74, 0.22)',
                  fontWeight: 600,
                }}
              >
                Submit
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default TinBday;